import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { SearchIcon, DatabaseIcon, DownloadIcon, ArrowRightIcon, CheckCircle2Icon } from 'lucide-react';
const topSteps = [{
  id: 1,
  icon: SearchIcon,
  title: 'Search local businesses',
  description: 'Find local businesses by location, category, revenue, or industry using our powerful API.'
}, {
  id: 2,
  icon: DatabaseIcon,
  title: 'Enrich contacts',
  description: 'Get verified emails, phones, and decision-maker contacts for your target accounts.'
}, {
  id: 3,
  icon: DownloadIcon,
  title: 'Export data',
  description: 'Export enriched local business contacts as CSV, or sync to Salesforce, HubSpot, and ad platforms like Meta Ads.'
}];
const containerVariants = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.2
    }
  }
};
const itemVariants = {
  hidden: {
    opacity: 0,
    y: 20
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5
    }
  }
};
export function WorkflowSection() {
  return <section className="py-24 bg-slate-50 border-y border-slate-100" data-id="element-5107">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-5108">
        <div className="text-center max-w-3xl mx-auto mb-20" data-id="element-5109">
          <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6 leading-tight" data-id="element-5110">
            From local business data to{' '}
            <span className="text-blue-600" data-id="element-5111">ad audiences</span> in minutes
          </h2>
          <p className="text-xl text-slate-600 leading-relaxed" data-id="element-5112">
            A seamless workflow to turn raw local business data into
            high-performing marketing campaigns.
          </p>
        </div>

        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} data-id="element-5113">
          {/* Steps 1-3: Horizontal flow */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-0 mb-10" data-id="element-5114">
            {topSteps.map((step, index) => {
            const Icon = step.icon;
            return <motion.div key={step.id} variants={itemVariants} className="relative flex flex-col items-center text-center px-6" data-id="element-5115">
                  {/* Arrow between steps (desktop) */}
                  {index < topSteps.length - 1 && <div className="hidden md:flex absolute top-10 -right-3 z-10 w-6 h-6 items-center justify-center" data-id="element-5116">
                      <ArrowRightIcon className="w-5 h-5 text-slate-300" data-id="element-5117" />
                    </div>}

                  {/* Icon */}
                  <div className="relative mb-5" data-id="element-5118">
                    <div className="w-20 h-20 rounded-2xl bg-white border-2 border-slate-200 shadow-sm flex items-center justify-center" data-id="element-5119">
                      <Icon className="w-9 h-9 text-blue-600" strokeWidth={1.5} data-id="element-5120" />
                    </div>
                    <div className="absolute -top-2 -right-2 w-7 h-7 rounded-full bg-blue-600 text-white flex items-center justify-center text-xs font-bold border-4 border-slate-50" data-id="element-5121">
                      {step.id}
                    </div>
                  </div>

                  <h3 className="text-lg font-bold text-slate-900 mb-2" data-id="element-5122">
                    {step.title}
                  </h3>
                  <p className="text-sm text-slate-600 leading-relaxed max-w-xs" data-id="element-5123">
                    {step.description}
                  </p>

                  {/* Mobile arrow */}
                  {index < topSteps.length - 1 && <div className="md:hidden mt-6 text-slate-300" data-id="element-5124">
                      <ArrowRightIcon className="w-5 h-5 rotate-90" data-id="element-5125" />
                    </div>}
                </motion.div>;
          })}
          </div>

          {/* Arrow from step 3 to step 4 */}
          <motion.div variants={itemVariants} className="flex justify-center my-6" data-id="element-5126">
            <div className="flex flex-col items-center" data-id="element-5127">
              <div className="w-0.5 h-10 bg-blue-600/30" data-id="element-5128" />
              <ArrowRightIcon className="w-6 h-6 text-blue-600/30 rotate-90 -mt-1" data-id="element-5129" />
            </div>
          </motion.div>

          {/* Step 4: Featured section with illustration */}
          <motion.div variants={itemVariants} className="bg-white rounded-3xl border border-slate-200 shadow-sm overflow-hidden" data-id="element-5130">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-0" data-id="element-5131">
              {/* Left: Text */}
              <div className="p-10 lg:p-14 flex flex-col justify-center" data-id="element-5132">
                <div className="flex items-center gap-3 mb-6" data-id="element-5133">
                  <div className="w-10 h-10 rounded-full bg-blue-600 text-white flex items-center justify-center text-sm font-bold" data-id="element-5134">
                    4
                  </div>
                  <span className="text-sm font-bold text-blue-600 uppercase tracking-wider" data-id="element-5135">
                    Final step
                  </span>
                </div>
                <h3 className="text-2xl lg:text-3xl font-bold text-slate-900 mb-4" data-id="element-5136">
                  Activate audiences
                </h3>
                <p className="text-lg text-slate-600 leading-relaxed mb-8" data-id="element-5137">
                  Upload to ad platforms like Facebook Ads (Meta Ads) as custom
                  audiences to run highly targeted local campaigns. Your
                  enriched contacts become precision ad audiences instantly.
                </p>
                <div className="flex flex-wrap gap-3" data-id="element-5138">
                  <span className="inline-flex items-center gap-1.5 text-sm font-medium text-green-700 bg-green-50 px-3 py-1.5 rounded-full" data-id="element-5139">
                    <CheckCircle2Icon className="w-4 h-4" data-id="element-5140" /> 92% match rate
                  </span>
                  <span className="inline-flex items-center gap-1.5 text-sm font-medium text-blue-700 bg-blue-50 px-3 py-1.5 rounded-full" data-id="element-5141">
                    <CheckCircle2Icon className="w-4 h-4" data-id="element-5142" /> Custom audiences
                  </span>
                  <span className="inline-flex items-center gap-1.5 text-sm font-medium text-purple-700 bg-purple-50 px-3 py-1.5 rounded-full" data-id="element-5143">
                    <CheckCircle2Icon className="w-4 h-4" data-id="element-5144" /> Instant upload
                  </span>
                </div>
              </div>

              {/* Right: Meta Ads illustration */}
              <div className="bg-slate-50 p-8 lg:p-12 flex items-center justify-center border-t lg:border-t-0 lg:border-l border-slate-100" data-id="element-5145">
                <div className="w-full max-w-sm bg-white rounded-2xl border border-slate-200 shadow-sm p-5 text-left" data-id="element-5146">
                  <div className="flex items-center gap-2 mb-4" data-id="element-5147">
                    <div className="w-6 h-6 rounded-full bg-blue-600 flex items-center justify-center" data-id="element-5148">
                      <span className="text-white text-[10px] font-bold" data-id="element-5149">
                        f
                      </span>
                    </div>
                    <span className="text-sm font-bold text-slate-800" data-id="element-5150">
                      Meta Ads (Facebook Ads)
                    </span>
                  </div>

                  <div className="bg-blue-50 rounded-xl p-4 mb-4" data-id="element-5151">
                    <div className="text-xs font-semibold text-blue-800 mb-2" data-id="element-5152">
                      Custom Audience upload
                    </div>
                    <div className="flex items-center gap-2 mb-2" data-id="element-5153">
                      <div className="flex-1 h-2 bg-blue-200 rounded-full overflow-hidden" data-id="element-5154">
                        <motion.div initial={{
                        width: 0
                      }} whileInView={{
                        width: '100%'
                      }} viewport={{
                        once: true
                      }} transition={{
                        duration: 1.5,
                        ease: 'easeOut',
                        delay: 0.5
                      }} className="h-full bg-blue-500 rounded-full" data-id="element-5155" />
                      </div>
                      <span className="text-xs text-blue-600 font-bold" data-id="element-5156">
                        100%
                      </span>
                    </div>
                    <div className="text-xs text-blue-600" data-id="element-5157">
                      245 leads matched · 92% match rate
                    </div>
                  </div>

                  <div className="space-y-2" data-id="element-5158">
                    {[{
                    name: 'Austin Restaurants',
                    size: '245',
                    status: 'Active',
                    statusColor: 'bg-green-50 text-green-600'
                  }, {
                    name: 'TX Coffee Shops',
                    size: '180',
                    status: 'Ready',
                    statusColor: 'bg-amber-50 text-amber-600'
                  }, {
                    name: 'Miami Bakeries',
                    size: '312',
                    status: 'Active',
                    statusColor: 'bg-green-50 text-green-600'
                  }].map((a, i) => <div key={i} className="flex items-center justify-between py-2 px-3 rounded-lg bg-slate-50 border border-slate-100" data-id="element-5159">
                        <span className="text-xs font-semibold text-slate-700" data-id="element-5160">
                          {a.name}
                        </span>
                        <div className="flex items-center gap-2" data-id="element-5161">
                          <span className="text-[10px] text-slate-400" data-id="element-5162">
                            {a.size} leads
                          </span>
                          <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${a.statusColor}`} data-id="element-5163">
                            {a.status}
                          </span>
                        </div>
                      </div>)}
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>;
}